import { PropType, computed, defineComponent, onUnmounted, reactive, ref, watch } from 'vue'
import { Store } from 'pinia'
import { bd09toGcj02, gcj02ToBd09 } from '@/utils/coordinateTransform'
import { ResourceItemModel } from '../../iconRender/icon.model'
import { Point } from '../../../contract/map'
import { ChargeMapContext } from '../../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../../nsh5/localStores/component'
import { getDistanceByViewport } from '../../chargerMap/mapUtils'
import { getPowerAroundV2, getPowerSwapDetail } from '../../../apis/map.api'
import { DEFAULT_DISTRICT_ZOOM_LEVEL } from '../../../config/zoomLevelConfig'

/**
 * 心愿站大ICON渲染功能模块
 */
export type WishIconRenderProps = {
  onClickedVglMarker: (customMarkerData: Point | null) => void
}
export default defineComponent({
  name: 'wish-icon-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },

  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)

    const clickedMarker = ref<Point | null>() // 选中的点marker信息
    const idBubbleMap = {} as { [key: string]: any } // 用于记录点击的点的id和气泡信息
    const state = reactive({
      distanceCatch: undefined as undefined | number,
      zoomLevelDistanceCatch: undefined as undefined | number,
    })

    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig

    /**
     * 心愿站画换电站和电区房覆盖圈
     * @param zoomLevel 缩放层级
     */
    const drawSwapAndCircle = async (zoomLevel: number) => {
      if (componentsConfig?.wishpower?.properties?.ps_district_house_circle) {
        // 通过缓存的距离和缩放层级判断是否需要重新获取
        const distance =
          zoomLevel === state.zoomLevelDistanceCatch && state.distanceCatch
            ? state.distanceCatch
            : getDistanceByViewport(mapContextRef.value!)
        state.distanceCatch = distance
        state.zoomLevelDistanceCatch = zoomLevel
        // 获取周围换电站数据
        const centerLocation = mapContextRef.value?.getCenter() as Point
        const loc = bd09toGcj02(centerLocation)
        const res = await getPowerAroundV2(
          zoomLevel,
          {
            'recharge|ps': null,
            // recharge: {
            //   resource_types: 'PS',
            // },
          },
          loc,
          distance
        )
        if (res.isValid() && res.data?.powers) {
          addResourceCircleAll(res.data.powers)
        }
      }
    }

    const addResourceCircleAll = (resources: ResourceItemModel[]) => {
      mapContextRef.value?.removeAllSurroundCircle() // 补充再清除
      for (const resource of resources) {
        const position = resource.location.split(',') // gcj
        const point = gcj02ToBd09({
          lng: Number(position[0]),
          lat: Number(position[1]),
        }) // bd
        mapContextRef.value?.addSwapCircleAll(point, 3000, resource.id)
      }
    }

    const renderVglMarkers = () => {
      mapContextRef.value?.addMapVglMarkers([...props.componentStore.allResources.swap])
    }

    // ------------暴露出去的方法--------------
    const onClickedVglMarker = async (customMarkerData: Point | null) => {
      if (
        !customMarkerData ||
        !customMarkerData.id ||
        clickedMarker.value?.id == customMarkerData.id
      ) {
        props.componentStore.unsetDestLocationByPin()
        mapContextRef.value?.resetMapVglMarkers()
        clickedMarker.value = null
        // 防止对同一个点多次点击
        return
      }
      clickedMarker.value = customMarkerData
      if (!Object.prototype.hasOwnProperty.call(idBubbleMap, customMarkerData.id)) {
        const res = await getPowerSwapDetail(customMarkerData.id)
        idBubbleMap[customMarkerData.id] = {
          name: res.data.name || '获取信息失败',
          lng: customMarkerData.lng,
          lat: customMarkerData.lat,
          address: res.data.address || '获取地址失败',
        }
      }
      // 设置目的地，显示小气泡
      props.componentStore.setDestLocationByPin(idBubbleMap[customMarkerData.id])
    }

    props.componentStore.bus.on('wishIconRenderClickedVglMarker', onClickedVglMarker)

    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
    }

    expose({
      onClickedVglMarker,
    })

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
    }

    onUnmounted(() => {
      clickedMarker.value && onClickedVglMarker(clickedMarker.value as Point) // 变大的ICON先变小
      mapContextRef.value?.removeAllSurroundCircle()
      clickedMarker.value = null
      setTimeout(() => {
        // 让around接口加载好再切换
        mapContextRef.value?.hideMapVglMarkers()
      }, 2000)
      props.componentStore.bus.off('wishIconRenderClickedVglMarker')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      initEventListener()
      renderVglMarkers()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => zoomLevel.value,
      (newVal, oldVal) => {
        if (zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL) {
          if (!oldVal || oldVal <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
            renderVglMarkers()
          }
          if (zoomLevel.value > 12.5) {
            // 出现电区房的圈
            drawSwapAndCircle(zoomLevel.value)
          } else {
            mapContextRef.value?.removeAllSurroundCircle()
          }
        } else {
          props.componentStore.unsetDestLocationByPin()
          mapContextRef.value?.removeAllSurroundCircle()
          clickedMarker.value = null
          mapContextRef.value?.hideMapVglMarkers()
        }
      },
      {
        immediate: true,
      }
    )

    return () => {}
  },
})
